import { GridRowHeightParams, gridClasses, DataGridProProps as MuiDataGridProProps } from '@mui/x-data-grid-pro';
import { useMemo } from 'react';
import { DataTableExpandableCell } from './DataTableExpandableCell';
import { DATA_TABLE_COMPACT_DENSITY_FACTOR, DataTableProps } from './model';

export type UseExpandableCellsProps = Required<
  Pick<DataTableProps, 'rowHeight' | 'columns' | 'enableExpandableCells' | 'compact'>
> &
  Pick<DataTableProps, 'getRowHeight'>;

export interface UseExpandableCellsResponse extends Pick<MuiDataGridProProps, 'columns' | 'sx' | 'className'> {}

export const ENABLE_EXPANDABLE_CELLS_CLASS_NAME = `${gridClasses.root}--expandableCells`;

/**
 * Classname that can be helpful for propagating the truncation of text (and turning it off on hover)
 * in more complex use cases that use `renderCell` and cannot rely on the default, built-in behavior.
 *
 * Without this, there can be some annoying edge cases when the user hovers over the edges of the containing
 * content (e.g. border, padding) that wouldn't be covered by checking for hover on the nested dom nodes.
 */
export const EXPANDABLE_CELL_NESTED_TEXT_CONTENT_CLASS_NAME = `${gridClasses.cellContent}--nestedText`;

/**
 * Classname that can be helpful for rendering of a list of items via flexbox in an expandable table
 * cell. It will set `flex-wrap` to `wrap` when the cell is expanded and to `nowrap` when it is
 * not.
 */
export const EXPANDABLE_CELL_FLEX_WRAP_CLASS_NAME = `${gridClasses.cellContent}--flexWrap`;

/**
 * Hook that does additional processing and calculations necessary when `enableExpandableCells` is
 * true.
 */
export const useExpandableCells = ({
  enableExpandableCells,
  columns: initColumns,
  rowHeight: initRowHeight,
  getRowHeight,
  compact,
}: UseExpandableCellsProps): UseExpandableCellsResponse => {
  const columns: UseExpandableCellsProps['columns'] = useMemo(() => {
    if (!enableExpandableCells) {
      return initColumns;
    }

    return initColumns.map((column) => {
      const { disableExpandableCell, renderCell, ...otherColumn } = column;

      if (disableExpandableCell) {
        return column;
      }

      const updatedColumn: UseExpandableCellsProps['columns'][number] = {
        ...otherColumn,
        renderCell: (params) => {
          const { formattedValue, value } = params;

          let cellChildren: React.ReactNode;
          if (renderCell) {
            cellChildren = renderCell(params);
          } else {
            // Matching some similar code from MUI.
            // https://github.com/mui/mui-x/blob/v6.x/packages/grid/x-data-grid/src/components/cell/GridCell.tsx#L619
            // https://github.com/mui/mui-x/blob/v6.x/packages/grid/x-data-grid/src/components/cell/GridCell.tsx#L412C5-L412C51
            const valueToRender = formattedValue == null ? value : formattedValue;
            cellChildren = valueToRender?.toString();
          }

          // Wrap custom cell renderers in a component that adds a consistent class name we can
          // hang on to enable the expanded cell functionality.
          return <DataTableExpandableCell>{cellChildren}</DataTableExpandableCell>;
        },
      };

      return updatedColumn;
    });
  }, [enableExpandableCells, initColumns]);

  const sx: UseExpandableCellsResponse['sx'] = useMemo(() => {
    if (!enableExpandableCells) {
      return undefined;
    }

    // We need to account for the fact that MUI modifies the row height based on an internal factor value when
    // the density is modified.
    const rowHeightFactor = compact ? DATA_TABLE_COMPACT_DENSITY_FACTOR : 1;
    const defaultRowHeight = initRowHeight * rowHeightFactor;
    let rowHeight = defaultRowHeight;

    if (getRowHeight) {
      // If `getRowHeight` function is defined, see if it's possible to fetch a numeric value from it
      // that would be more appropriate to use than the default MUI behavior.
      try {
        // Forced to cast here because we cannot provide individual row entry information in this
        // use case. This is also why this in a try/catch to try to avoid breaking in more complex cases.
        const calculatedRowHeight = getRowHeight({ densityFactor: rowHeightFactor } as GridRowHeightParams);

        if (typeof calculatedRowHeight === 'number' && calculatedRowHeight > 0) {
          rowHeight = calculatedRowHeight;
        }
      } catch (_e) {
        // Do nothing. We fall back to the basic row height calculations if `getRowHeight` throws an error with the limited
        // params we're able to pass to it in this context.
      }
    }

    // Styles that need to be dynamic because they depend on the `rowHeight`. All other styles are nested in the
    // `styled` configuration in `DataTable`.
    // NOTE: we will very likely need to re-work this logic when we do the next major MUI upgrade because
    // they change some of the default cell rendering logic and class names.
    return {
      [`&.${ENABLE_EXPANDABLE_CELLS_CLASS_NAME}`]: {
        [`& .${gridClasses.cell}:hover`]: {
          [`& .${gridClasses.cellContent}`]: {
            // Padding styles attempt to simulate the vertically centered text when expanded on hover in an attempt
            // to avoid the content hopping around on hover. This makes some assumptions about the content following
            // the default table styles and may not work as expected when the cell content is heavily customized with
            // a `renderCell` configuration. The 1px subtraction accounts for the top border.
            paddingTop: (theme) => `calc((${rowHeight}px - 1px - ${theme.typography.body2.lineHeight}) / 2)`,
            paddingBottom: (theme) => `calc((${rowHeight}px - 1px - ${theme.typography.body2.lineHeight}) / 2)`,
          },
        },
      },
    };
  }, [compact, enableExpandableCells, getRowHeight, initRowHeight]);

  return {
    // Class name we use to gate a bunch of custom styles for expanding, so they're only enabled when this feature
    // is turned on.
    className: enableExpandableCells ? ENABLE_EXPANDABLE_CELLS_CLASS_NAME : undefined,
    columns,
    sx,
  };
};
